import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Flame, 
  Heart, 
  RefreshCw, 
  Trophy,
  Calendar,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  Lock,
  Crown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const milestones = [
  { days: 7, label: '1 Week', icon: Shield, message: 'First week complete! Your boundaries are getting stronger.' },
  { days: 14, label: '2 Weeks', icon: Heart, message: 'Two weeks of choosing yourself. Your heart is healing.' },
  { days: 30, label: '1 Month', icon: Trophy, message: 'One month! The trauma bond is weakening.' },
  { days: 60, label: '2 Months', icon: Sparkles, message: 'Two months of peace. You\'re reclaiming your life.' },
  { days: 90, label: '3 Months', icon: Flame, message: 'Three months! Major healing milestone achieved.' },
];

export default function NoContactTracker() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [urgeDialogOpen, setUrgeDialogOpen] = useState(false);
  const [urgeNote, setUrgeNote] = useState('');
  const isPremium = user?.is_premium;

  useEffect(() => {
    fetchTracker();
  }, []);

  const fetchTracker = async () => {
    try {
      const res = await axios.get(`${API}/tracker/no-contact`);
      setTracker(res.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load tracker');
    } finally {
      setLoading(false);
    }
  };

  const handleLogUrge = async () => {
    try {
      const res = await axios.post(`${API}/tracker/no-contact`, {
        log_urge: true,
        urge_note: urgeNote || null
      });
      setTracker(res.data);
      setUrgeDialogOpen(false);
      setUrgeNote('');
      toast.success('Urge logged! You resisted. That\'s victory!');
    } catch (error) {
      toast.error('Failed to log urge');
    }
  };

  const handleReset = async () => {
    try {
      const res = await axios.post(`${API}/tracker/no-contact`, {
        reset: true
      });
      setTracker(res.data);
      setResetDialogOpen(false);
      toast('Counter reset. Every new start is a new chance.', {
        icon: '💜'
      });
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const getNextMilestone = () => {
    if (!tracker) return milestones[0];
    return milestones.find(m => m.days > tracker.days) || milestones[milestones.length - 1];
  };

  const getMilestoneProgress = () => {
    if (!tracker) return 0;
    const next = getNextMilestone();
    const prev = milestones.filter(m => m.days <= tracker.days).pop();
    const prevDays = prev?.days || 0;
    return ((tracker.days - prevDays) / (next.days - prevDays)) * 100;
  };

  const getCompletedMilestones = () => {
    if (!tracker) return [];
    return milestones.filter(m => m.days <= tracker.days);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const nextMilestone = getNextMilestone();
  const NextIcon = nextMilestone.icon;

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="no-contact-page">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-3">
            No-Contact Tracker
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Every day of no contact is a day of healing. Track your progress and celebrate your victories.
          </p>
        </div>

        {/* Main Counter */}
        <div className="crown-card p-8 lg:p-12 text-center mb-8" data-testid="day-counter">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 mb-6">
            <Flame className="w-12 h-12 text-white" />
          </div>
          <div className="mb-4">
            <span className="text-6xl lg:text-8xl font-bold text-foreground">{tracker?.days || 0}</span>
            <span className="text-2xl text-muted-foreground ml-2">days</span>
          </div>
          <p className="text-lg text-muted-foreground mb-2">of choosing peace over chaos</p>
          <p className="text-sm text-muted-foreground">
            Started: {new Date(tracker?.start_date).toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric' 
            })}
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="crown-card p-5 text-center">
            <Heart className="w-8 h-8 text-rose-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-foreground">{tracker?.urges_resisted || 0}</p>
            <p className="text-sm text-muted-foreground">Urges Resisted</p>
          </div>
          <div className="crown-card p-5 text-center">
            <Trophy className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-foreground">{getCompletedMilestones().length}</p>
            <p className="text-sm text-muted-foreground">Milestones Reached</p>
          </div>
        </div>

        {/* Next Milestone */}
        <div className="glass-gold rounded-2xl p-6 mb-8" data-testid="next-milestone">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <NextIcon className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-amber-700 dark:text-amber-300">Next Milestone</p>
                <p className="font-semibold text-foreground">{nextMilestone.label}</p>
              </div>
            </div>
            <span className="text-2xl font-bold text-amber-600">
              {nextMilestone.days - (tracker?.days || 0)} days
            </span>
          </div>
          <Progress value={getMilestoneProgress()} className="h-3 mb-3" />
          <p className="text-sm text-muted-foreground">{nextMilestone.message}</p>
        </div>

        {/* Action Buttons */}
        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          {isPremium ? (
            <Button
              onClick={() => setUrgeDialogOpen(true)}
              data-testid="log-urge-btn"
              className="w-full py-6 rounded-xl bg-primary hover:bg-primary/90 text-lg"
            >
              <Shield className="w-5 h-5 mr-2" />
              I Resisted an Urge
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/subscription')}
              variant="outline"
              className="w-full py-6 rounded-xl border-2 border-dashed text-lg"
            >
              <Lock className="w-5 h-5 mr-2" />
              Urge Logging (Premium)
            </Button>
          )}
          <Button
            onClick={() => setResetDialogOpen(true)}
            variant="outline"
            data-testid="reset-btn"
            className="w-full py-6 rounded-xl border-2 border-destructive text-destructive hover:bg-destructive/5 text-lg"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Reset Counter
          </Button>
        </div>

        {/* Milestones Timeline */}
        <div>
          <h2 className="font-serif text-2xl font-semibold text-foreground mb-6">Healing Milestones</h2>
          <div className="space-y-4">
            {milestones.map((milestone, idx) => {
              const Icon = milestone.icon;
              const isCompleted = tracker && tracker.days >= milestone.days;
              return (
                <div 
                  key={milestone.days}
                  className={`crown-card p-5 flex items-center gap-4 transition-all ${
                    isCompleted ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10' : ''
                  }`}
                  data-testid={`milestone-${milestone.days}`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    isCompleted 
                      ? 'bg-green-500 text-white' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {isCompleted ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">{milestone.label}</p>
                    <p className="text-sm text-muted-foreground">{milestone.message}</p>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">{milestone.days} days</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Urge Dialog */}
        <Dialog open={urgeDialogOpen} onOpenChange={setUrgeDialogOpen}>
          <DialogContent data-testid="urge-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-serif text-xl">
                <Shield className="w-6 h-6 text-primary" />
                You Resisted! 
              </DialogTitle>
              <DialogDescription>
                That took strength. Want to note what triggered it?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <Textarea
                data-testid="urge-note-input"
                placeholder="What triggered the urge? (Optional)"
                value={urgeNote}
                onChange={(e) => setUrgeNote(e.target.value)}
                className="min-h-[100px]"
              />
              <div className="flex gap-3">
                <Button
                  onClick={() => setUrgeDialogOpen(false)}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleLogUrge}
                  data-testid="confirm-urge-btn"
                  className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                >
                  Log Victory
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
          <DialogContent data-testid="reset-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-serif text-xl">
                <AlertTriangle className="w-6 h-6 text-destructive" />
                Reset Counter?
              </DialogTitle>
              <DialogDescription>
                It's okay. Healing isn't linear. Every restart is still progress forward.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setResetDialogOpen(false)}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Keep Going
              </Button>
              <Button
                onClick={handleReset}
                data-testid="confirm-reset-btn"
                variant="destructive"
                className="flex-1 rounded-full"
              >
                Reset & Restart
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
